<script setup lang="ts">
import Popover from 'primevue/popover'
import { computed, ref, useId } from 'vue'

import { useI18n } from '@/app/i18n'
import { switchSoftware } from '@/app/session'
import { useAuthStore } from '@/app/stores/auth'
import type { SoftwareCode } from '@/app/stores/session'
import { errorMessage, toast } from '@/shared/composables/useToast'
import { SOFTWARE_ORDER as ORDER, softwareColor, softwareMark as markOf } from '@/shared/scope/scope'
import AppIcon from '@/shared/ui/AppIcon.vue'

/**
 * The software block at the top of the vertical nav (wp/shell §4.18, redesigned 2026-10): a card with the current
 * software's coloured tile with its two-letter mark (as the setup wizard's start step), its name and its tagline (the workspace is the navbar's, 2026-10), which opens a list of the user's softwares (tile,
 * name, one-line descriptor, check on the current one) in the legacy order `ac, hr, re, on, en`, then the others.
 * With a single software (a Freelancer as candidate or employee) the card is static: no chevron, no list. Collapsed,
 * only the tile shows; the list still opens from it.
 *
 * Colours: the software's own (`softwareColors`); `ca` / `em` take the first and second position colours, as they
 * did for a Freelancer in legacy.
 */
const props = defineProps<{ software: SoftwareCode | null; mini: boolean }>()
const { t } = useI18n()
const auth = useAuthStore()
const switching = ref(false)
const open = ref(false)
const popover = ref<InstanceType<typeof Popover> | null>(null)
const listId = `software-list-${useId()}`

const titles = computed<Record<SoftwareCode, string>>(() => ({
  re: t('common.nav.recruiting'),
  on: t('common.nav.onboarding'),
  hr: t('common.nav.humanResources'),
  en: t('common.nav.engagement'),
  ac: t('common.nav.accounting'),
  pm: t('shell.nav.title.projects'),
  ca: t('shell.nav.title.candidate'),
  em: t('shell.nav.title.employee'),
}))

const taglines = computed<Record<SoftwareCode, string>>(() => ({
  re: t('shell.nav.software.re'),
  on: t('shell.nav.software.on'),
  hr: t('shell.nav.software.hr'),
  en: t('shell.nav.software.en'),
  ac: t('shell.nav.software.ac'),
  pm: t('shell.nav.software.pm'),
  ca: t('shell.nav.software.ca'),
  em: t('shell.nav.software.em'),
}))

interface Option {
  code: SoftwareCode
  title: string
  tagline: string
  /** The two-letter mark on the tile (the setup wizard's start step uses the same). */
  mark: string
  color: string
  current: boolean
}

const options = computed<Option[]>(() => {
  const softwares = [...(auth.session?.softwares ?? [])].sort((a, b) => {
    const rank = (code: SoftwareCode) => (ORDER.includes(code) ? ORDER.indexOf(code) : Number.MAX_SAFE_INTEGER)
    return rank(a.short) - rank(b.short)
  })
  return softwares.map((software, index) => ({
    code: software.short,
    title: titles.value[software.short],
    tagline: taglines.value[software.short],
    mark: markOf(software.short),
    color: softwareColor(software.short, index),
    current: software.short === props.software,
  }))
})

const current = computed<Option | null>(() => {
  const found = options.value.find((option) => option.current)
  if (found) return found
  if (!props.software) return null
  // A software the session does not list (a shared route): shown, not switchable to.
  return {
    code: props.software,
    title: titles.value[props.software],
    tagline: taglines.value[props.software],
    mark: markOf(props.software),
    color: softwareColor(props.software),
    current: true,
  }
})
const switchable = computed(() => options.value.length > 1)

function toggle(event: Event): void {
  if (!switchable.value) return
  popover.value?.toggle(event)
}

async function choose(code: SoftwareCode): Promise<void> {
  popover.value?.hide()
  if (switching.value || code === props.software) return
  switching.value = true
  try {
    await switchSoftware(code)
  } catch (error) {
    // Legacy had no catch (nothing shown); the API message is more useful than silence.
    toast.danger(errorMessage(error))
  } finally {
    switching.value = false
  }
}
</script>

<template>
  <div v-if="current" class="software-switcher" :class="{ 'is-mini': mini }" data-testid="software-switcher">
    <button
      type="button"
      class="software-switcher__card"
      :class="{ 'is-static': !switchable }"
      :aria-label="mini || !switchable ? current.title : undefined"
      :aria-haspopup="switchable ? 'listbox' : undefined"
      :aria-expanded="switchable ? open : undefined"
      :aria-controls="switchable && open ? listId : undefined"
      :disabled="switching"
      data-testid="software-switcher-open"
      @click="toggle"
    >
      <span class="software-switcher__tile" :style="{ background: current.color }" aria-hidden="true">
        {{ current.mark }}
      </span>
      <span class="software-switcher__text">
        <span class="software-switcher__name" data-testid="software-switcher-name">{{ current.title }}</span>
        <span class="software-switcher__tagline">{{ current.tagline }}</span>
      </span>
      <AppIcon v-if="switchable" name="ChevronsUpDownIcon" :size="16" class="software-switcher__chevron" />
    </button>

    <Popover
      v-if="switchable"
      ref="popover"
      :base-z-index="1100"
      :pt="{ root: { class: 'shell-popover software-switcher__panel', 'data-testid': 'software-switcher-list' } }"
      @show="open = true"
      @hide="open = false"
    >
      <div class="software-switcher__heading">{{ t('shell.nav.action.switchSoftware') }}</div>
      <ul
        :id="listId"
        role="listbox"
        :aria-label="t('shell.nav.action.switchSoftware')"
        class="software-switcher__list"
      >
        <li v-for="option in options" :key="option.code" role="presentation">
          <button
            type="button"
            role="option"
            class="software-switcher__option"
            :class="{ 'is-current': option.current }"
            :aria-selected="option.current"
            :data-software="option.code"
            @click="choose(option.code)"
          >
            <span class="software-switcher__tile" :style="{ background: option.color }" aria-hidden="true">
              {{ option.mark }}
            </span>
            <span class="software-switcher__text">
              <span class="software-switcher__name">{{ option.title }}</span>
              <span class="software-switcher__tagline">{{ option.tagline }}</span>
            </span>
            <AppIcon v-if="option.current" name="CheckIcon" :size="18" class="software-switcher__check" />
          </button>
        </li>
      </ul>
    </Popover>
  </div>
</template>

<style scoped>
/* Below the card, a fixed gap keeps the scrolled menu from running into it. */
.software-switcher {
  padding: 0.5rem 1rem 0.75rem;
}
.software-switcher.is-mini {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 0.75rem;
}
.software-switcher__card {
  display: flex;
  gap: 0.7rem;
  align-items: center;
  width: 100%;
  padding: 0.43rem;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  transition:
    border-color 0.2s ease,
    background 0.2s ease;
}
.software-switcher__card:hover:not(.is-static),
.software-switcher__card[aria-expanded='true'] {
  background: var(--p-surface-50);
  border-color: var(--p-surface-300);
}
.software-switcher__card.is-static {
  cursor: default;
}
.software-switcher__card:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
/* Collapsed: the tile alone, with the same padding and border as the full card, so the card keeps its height. */
.software-switcher.is-mini .software-switcher__card {
  justify-content: center;
  width: auto;
}
.software-switcher.is-mini .software-switcher__text,
.software-switcher.is-mini .software-switcher__chevron {
  display: none;
}
.software-switcher__tile {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 1.07rem;
  font-weight: 600;
  color: #fff;
  border-radius: calc(var(--p-border-radius-lg) - 2px);
}
.software-switcher__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.software-switcher__name {
  overflow: hidden;
  font-size: 1rem;
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--p-surface-950);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.software-switcher__tagline {
  overflow: hidden;
  font-size: 0.929rem;
  font-weight: 500;
  color: var(--p-surface-600);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.software-switcher__chevron {
  flex: 0 0 auto;
  color: var(--p-surface-500);
}
.software-switcher__heading {
  padding: 0.5rem 0.75rem 0.4rem;
  font-size: 0.786rem;
  font-weight: 600;
  color: var(--p-surface-500);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.software-switcher__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.software-switcher__option {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.6rem 0.75rem;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: calc(var(--p-border-radius-lg) - 2px);
}
.software-switcher__option:hover,
.software-switcher__option:focus-visible {
  background: var(--p-surface-100);
  outline: none;
}
.software-switcher__option.is-current {
  background: color-mix(in srgb, var(--p-primary-500) 10%, var(--p-surface-0));
}
.software-switcher__check {
  flex: 0 0 auto;
  color: var(--p-primary-500);
}
</style>

<!-- The list panel is teleported to the body; its frame is the shell's `.shell-popover` (theme/tailwind.css). -->
